/**
 * IndexedDB cache utility for large application data
 * Can handle 40-50 MB easily (much larger than sessionStorage's 5-10 MB limit)
 */
const DB_NAME = 'rbac_cache';
const STORE_NAME = 'DATA_STORE';
const DB_VERSION = 1;
/**
 * Create object store if it doesn't exist
 */
const createObjectStoreIfNeeded = (db) => {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
    }
};
/**
 * Recreate database when object store is missing
 */
const recreateDatabase = () => {
    return new Promise((resolve, reject) => {
        const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
        deleteRequest.onsuccess = () => {
            const retryRequest = indexedDB.open(DB_NAME, DB_VERSION);
            retryRequest.onerror = () => reject(new Error('Failed to recreate IndexedDB'));
            retryRequest.onsuccess = () => resolve(retryRequest.result);
            retryRequest.onupgradeneeded = (event) => {
                const newDb = event.target.result;
                createObjectStoreIfNeeded(newDb);
            };
        };
        deleteRequest.onerror = () => reject(new Error('Failed to delete corrupted IndexedDB'));
    });
};
/**
 * Handle successful database open
 */
const handleDatabaseOpen = (db, resolve, reject) => {
    if (db.objectStoreNames.contains(STORE_NAME)) {
        resolve(db);
        return;
    }
    // Object store is missing, need to recreate database
    db.close();
    recreateDatabase().then(resolve).catch(reject);
};
/**
 * Open IndexedDB connection
 */
const openDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onerror = () => reject(new Error(request.error?.message || 'Failed to open IndexedDB'));
        request.onsuccess = () => {
            handleDatabaseOpen(request.result, resolve, reject);
        };
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            createObjectStoreIfNeeded(db);
        };
    });
};
/**
 * Save data to IndexedDB cache
 */
export const saveToCache = async (key, data) => {
    try {
        const db = await openDB();
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const cachedData = {
            data,
            timestamp: Date.now()
        };
        return new Promise((resolve, reject) => {
            const request = store.put(cachedData, key);
            request.onsuccess = () => {
                resolve();
            };
            request.onerror = () => reject(new Error(request.error?.message || 'Failed to save to cache'));
        });
    }
    catch (error) {
        console.error('Failed to save to IndexedDB:', error);
        throw error;
    }
};
/**
 * Get data from IndexedDB cache
 * Returns cached data without expiration (persists until logout)
 */
export const getFromCache = async (key) => {
    try {
        const db = await openDB();
        const transaction = db.transaction(STORE_NAME, 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        return new Promise((resolve, reject) => {
            const request = store.get(key);
            request.onsuccess = () => {
                const result = request.result;
                if (!result) {
                    resolve(null);
                    return;
                }
                resolve(result.data);
            };
            request.onerror = () => reject(new Error(request.error?.message || 'Failed to get from cache'));
        });
    }
    catch (error) {
        console.error('Failed to get from IndexedDB:', error);
        return null;
    }
};
/**
 * Clear all cached data
 */
export const clearCache = async () => {
    try {
        const db = await openDB();
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        return new Promise((resolve, reject) => {
            const request = store.clear();
            request.onsuccess = () => {
                resolve();
            };
            request.onerror = () => reject(new Error(request.error?.message || 'Failed to clear cache'));
        });
    }
    catch (error) {
        console.error('Failed to clear IndexedDB:', error);
        throw error;
    }
};
/**
 * Remove specific cached items by keys
 */
export const removeCacheKeys = async (keys) => {
    try {
        const db = await openDB();
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        return new Promise((resolve) => {
            const total = keys.length;
            if (total === 0) {
                resolve();
                return;
            }
            let completed = 0;
            const handleComplete = () => {
                completed++;
                if (completed === total) {
                    resolve();
                }
            };
            for (const key of keys) {
                const request = store.delete(key);
                request.onsuccess = handleComplete;
                request.onerror = handleComplete; // Continue even if some delete operations failed
            }
        });
    }
    catch (error) {
        console.error('Failed to remove cache keys:', error);
        throw error;
    }
};
/**
 * Get cache size (approximate)
 */
export const getCacheSize = async () => {
    try {
        if ('storage' in navigator && 'estimate' in navigator.storage) {
            const estimate = await navigator.storage.estimate();
            return estimate.usage || 0;
        }
        return 0;
    }
    catch (error) {
        console.error('Failed to estimate cache size:', error);
        return 0;
    }
};
